import React, { useContext, useEffect, useState } from 'react';
import flex from "../../styles/flex";
import { api, EventCategory, IDeleteWebHookEndPoint, IGetWebHookEndPointById, WebHook } from '../../logic/api';
import WebhooksListItem from './WebhooksListItem';
import { Button, Checkbox, Input, Typography, GridLegacy as Grid } from '@mui/material';
import { SessionContext } from '../../utility/useSession';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { ReleaseFlag, useReleaseManagement } from '@/utility/feature-management';


const WebhooksManager = () => {
    const session = useContext(SessionContext);
    const [initialUrl, setInitialUrl] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [selectedEndpointId, setSelectedEndpointId] = useState<number | null>(null);
    const [eventCategories, setEventCategories] = useState<string[]>([]);
    const [bearerToken, setBearerToken] = useState('');
    const [endpoints, setEndpoints] = useState<WebHook[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { t } = useTranslation();


    const getEndpointById = api.hooks.webHook.getEndPointById();
    const deleteEndpoint = api.hooks.webHook.deleteEndPoint();
    const createEndpoint = api.hooks.webHook.createEndPoint();
    const updateEndpoint = api.hooks.webHook.updateEndPoint();
    const listEndpoints = api.hooks.webHook.list();

    const isAssetTrackingWebHookReleased = useReleaseManagement(ReleaseFlag.releaseAssetTrackingWebhook);
    const isLocationsWebhookReleased = useReleaseManagement(ReleaseFlag.releaseLocationWebhooks);

    const eventCategoriesEnum = eventCategories.map(category => EventCategory[category as keyof typeof EventCategory]);

    const resetForm = () => {
        setInitialUrl('');
        setBearerToken('');
        setEventCategories([]);
        setIsEditing(false);
        setSelectedEndpointId(null);
        setIsSubmitted(false);
    };

    useEffect(() => {
        listEndpoints.send(session.current.context, { id: 0 }, (res) => {
            if (res && res.length > 0) {
                setSelectedEndpointId(res[0].id);
                setEndpoints(res);
            }
        });
    }, []);


    const handleEdit = (item: IGetWebHookEndPointById) => {
        getEndpointById.send(session.current.context, { id: item.id }, (res) => {
            if (res) {
                setSelectedEndpointId(res.id);
                setIsEditing(true);
                setShowForm(true);

                const eventCategoriesStringArray = res.categories.map(category => EventCategory[category]);

                setInitialUrl(res.endpoint);
                setBearerToken(res.bearerToken);
                setEventCategories(eventCategoriesStringArray);
            }
        });
    }

    function isValidUrl(string: string | URL) {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    }

    const isDuplicateUrl = (url: string, webHooks: WebHook[]) => {
        //make sure url does not match pre-existing webhook name
        if (isEditing && webHooks.some(webHook => webHook.id !== selectedEndpointId && webHook.endpoint === url)) {
            return true;
        }

        //make sure does not fail on url name not changing
        if (isEditing && webHooks.some(webHook => webHook.id === selectedEndpointId && webHook.endpoint === url)) {
            return false;
        }

        //make sure new url does not match pre-existing webhook name
        return !!webHooks.some(webHook => webHook.endpoint === url);
    }

    const isFormValid = () => {
        return initialUrl && ((!isEditing && bearerToken) || isEditing) && eventCategories && eventCategories.length > 0;
    };


    const handleSave = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitted(true);

        let isValid = true;

        if (!initialUrl || !isValidUrl(initialUrl) || isDuplicateUrl(initialUrl, endpoints)) {
            isValid = false;
        }

        if (!bearerToken && !isEditing) {
            isValid = false;
        }

        if (!eventCategories || eventCategories.length === 0) {
            isValid = false;
        }

        if (isValid) {
            if (isEditing && selectedEndpointId) {
                updateEndpoint.send(session.current.context, {
                    id: selectedEndpointId,
                    eventCategories: eventCategoriesEnum,
                    uri: initialUrl,
                    bearerToken,
                }, (res) => {
                    if (res) {
                        setIsEditing(false);
                        setEndpoints(prev => prev.map(endpoint => endpoint.id === res.id ? res : endpoint));

                        setInitialUrl('');
                        setBearerToken('');
                        setEventCategories([]);
                        setIsSubmitted(false);
                    }
                });
            } else {
                createEndpoint.send(session.current.context, {
                    eventCategories: eventCategoriesEnum,
                    bearerToken: bearerToken,
                    uri: initialUrl
                }, (res) => {
                    if (res) {
                        setIsEditing(false);
                        setEndpoints(prev => [...prev, res]);

                        setInitialUrl('');
                        setBearerToken('');
                        setEventCategories([]);
                        setIsSubmitted(false);
                    }
                });
            }
        }
    };


    const handleDelete = (parameters: IDeleteWebHookEndPoint) => {
        deleteEndpoint.send(session.current.context, parameters, () => {
            setSelectedEndpointId(null);
            setEndpoints(prev => prev.filter(endpoint => endpoint.id !== parameters.id));
            setEventCategories([]);
        });
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { value } = event.target;
        let newEventCategories = [...eventCategories];

        if (newEventCategories.includes(value)) {
            newEventCategories = newEventCategories.filter(category => category !== value);
        } else {
            newEventCategories.push(value);
        }

        setEventCategories(newEventCategories);
        setIsSubmitted(false);
    };

    return (
        <div style={{ width: "100%", maxWidth: 1200, display: "inline-block" }}>
            <div style={{ textAlign: "left" }}>
                <div style={flex.flexRows}>
                    <div style={flex.flexFill}>
                        <h2>{t("webhooks.manage")}</h2>
                    </div>
                </div>

                <div style={{ padding: 5, marginBottom: 15, textAlign: "right" }}>
                    <div style={flex.flexAuto}>
                        <Button
                            style={{ marginLeft: 10 }}
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                setShowForm(!showForm);
                                if (!showForm) {
                                    resetForm();
                                }
                            }}
                        >
                            {showForm ? (
                                <>
                                    <FiMinus style={{ marginRight: 10 }} />
                                    {t("general.close")}
                                </>
                            ) : (
                                <>
                                    <FiPlus style={{ marginRight: 10 }} />
                                    {t("webhooks.new")}
                                </>
                            )}
                        </Button>
                    </div>

                </div>

                {showForm &&
                    <form onSubmit={handleSave}>
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Typography variant="subtitle1">{t("webhooks.uri")}</Typography>
                                <Input
                                    placeholder={t("webhooks.edit")}
                                    style={{
                                        padding: 10,
                                        width: "100%",
                                        border: "1px solid gray",
                                        borderRadius: 5,
                                    }}
                                    value={initialUrl}
                                    onChange={(e) => { setInitialUrl(e.target.value); setIsSubmitted(false) }}
                                />
                                {isSubmitted && (!initialUrl || !isValidUrl(initialUrl) || isDuplicateUrl(initialUrl, endpoints)) && <p style={{ color: 'red' }}>{t("webhooks.uniqueUri")}</p>}
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle1">{t("webhooks.bearerToken")}</Typography>
                                <Input
                                    placeholder={t("webhooks.bearerToken") + "..."}
                                    style={{
                                        padding: 10,
                                        width: "100%",
                                        border: "1px solid gray",
                                        borderRadius: 5,
                                    }}
                                    value={bearerToken}
                                    onChange={(e) => { setBearerToken(e.target.value); setIsSubmitted(false); }}
                                />
                                {isSubmitted && !bearerToken && !isEditing && <p style={{ color: 'red' }}>{t("webhooks.bearerTokenReq")}</p>}

                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">{t("webhooks.eventCat")}</Typography>
                                {
                                    Object.entries(EventCategory)
                                        .filter(([key]) => isNaN(Number(key)))
                                        .map(([key], index) => {
                                            if (key.toLowerCase() === "assettracking" && !isAssetTrackingWebHookReleased) {
                                                return <></>
                                            }

                                            if (key.toLowerCase() === "location" && !isLocationsWebhookReleased) {
                                                return <></>
                                            }

                                            return (< div key={index} style={{ marginLeft: 15, alignItems: 'center' }}>
                                                <label style={{ textTransform: 'capitalize' }}>
                                                    <Checkbox
                                                        checked={eventCategories.includes(key)}
                                                        value={key}
                                                        onChange={handleCheckboxChange}
                                                    />
                                                    {key}
                                                </label>
                                                {isSubmitted && eventCategories.length === 0 && <p style={{ color: 'red' }}>{t("webhooks.selectOneCategory")}</p>}
                                            </div>)
                                        })
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    style={{ width: '100%', marginBottom: 15 }}
                                    variant="contained"
                                    color="secondary"
                                    type='submit'
                                    disabled={!isFormValid()}
                                >{isEditing ? t("webhooks.saveChanges") : t("webhooks.addNew")}</Button>
                            </Grid>
                        </Grid>
                    </form>
                }

                {
                    endpoints.length > 0 ? endpoints.map((item, index) => <WebhooksListItem key={index} item={item} onEdit={() => handleEdit(item)} onDelete={() => handleDelete({ id: item.id })} />) : <div>{t("webhooks.noWebhooks")}</div>
                }

            </div>
        </div >
    );


}

export default WebhooksManager;
